import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TermsOfService = () => {
  return (
    <div className="terms-of-service-page">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Terms of Service
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Last Updated: December 2024
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl mx-auto prose prose-lg">
            
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Agreement to Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms of Service ("Terms") constitute a legally binding agreement between you and Blackbird Trade Group ("Company," "we," "us," or "our") concerning your access to and use of our website and services.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                By accessing our website or engaging our services, you agree to be bound by these Terms. If you do not agree with these Terms, you must not access our website or use our services.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Services Description</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Blackbird Trade Group provides professional trade compliance advisory and consulting services, including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Product classification (HTS, ECCN, USML)</li>
                <li>Export controls and licensing (ITAR and EAR compliance)</li>
                <li>Foreign Trade Zone (FTZ) advisory and operations</li>
                <li>Voluntary disclosure strategy and preparation</li>
                <li>Trade compliance program development and auditing</li>
                <li>Managed trade compliance services</li>
                <li>Training and education services</li>
              </ul>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Professional Services Engagement</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Scope of Work</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                The specific scope, deliverables, timeline, and fees for professional services will be outlined in a separate engagement letter or statement of work. Services are provided on a consulting and advisory basis only.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Client Responsibilities</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Clients are responsible for:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Providing accurate, complete, and timely information</li>
                <li>Reviewing and approving deliverables in a timely manner</li>
                <li>Making final decisions regarding compliance strategies</li>
                <li>Implementing recommendations as appropriate for their business</li>
                <li>Maintaining compliance with applicable laws and regulations</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Independent Contractor</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Blackbird Trade Group operates as an independent contractor. Nothing in these Terms creates an employment, partnership, joint venture, or agency relationship between you and Blackbird Trade Group.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Fees and Payment</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Service fees will be specified in your engagement letter. Unless otherwise agreed in writing:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Fees are due upon receipt of invoice</li>
                <li>Payment terms are Net 30 days unless otherwise specified</li>
                <li>Late payments may incur interest charges at the rate of 1.5% per month or the maximum rate permitted by law, whichever is less</li>
                <li>All fees are exclusive of applicable taxes, which are the client's responsibility</li>
              </ul>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Intellectual Property Rights</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Our Content</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                All content on our website, including text, graphics, logos, images, and software, is the property of Blackbird Trade Group or its licensors and is protected by copyright, trademark, and other intellectual property laws.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Work Product</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Upon full payment of fees, you receive a non-exclusive, non-transferable license to use deliverables created specifically for you. We retain ownership of our methodologies, templates, and general knowledge.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Confidentiality</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We maintain strict confidentiality regarding client information and business matters. Specific confidentiality obligations will be outlined in your engagement letter or separate non-disclosure agreement.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may use anonymized case studies or examples for marketing purposes, provided no client-identifying information is disclosed without explicit written consent.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed mb-4 font-semibold">
                IMPORTANT: PLEASE READ THIS SECTION CAREFULLY.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                To the maximum extent permitted by applicable law:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Our services are provided on an "as is" and "as available" basis</li>
                <li>We make no warranties, express or implied, regarding the accuracy, completeness, or results of our services</li>
                <li>Our total liability for any claim arising from our services shall not exceed the fees paid by you for the specific services giving rise to the claim</li>
                <li>We shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including lost profits or business interruption</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our advice and recommendations are based on the information provided to us and applicable regulations as understood at the time of service. Regulatory interpretations and requirements may change. Clients are ultimately responsible for their own compliance decisions and implementations.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Indemnification</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You agree to indemnify, defend, and hold harmless Blackbird Trade Group from any claims, damages, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Your use of our services</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any laws or regulations</li>
                <li>Information you provide to us</li>
              </ul>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Termination</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Either party may terminate an engagement as specified in the engagement letter. Termination does not relieve you of the obligation to pay for services rendered prior to termination.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                We reserve the right to refuse service or terminate our relationship with any client at our discretion, with or without cause.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Governing Law and Dispute Resolution</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the State of Florida, United States, without regard to its conflict of law provisions.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Any disputes arising from these Terms or our services shall be resolved through good faith negotiation. If negotiation fails, disputes shall be submitted to binding arbitration in accordance with the rules of the American Arbitration Association.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Website Use</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You agree not to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Use our website for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with or disrupt the website or servers</li>
                <li>Collect or harvest information from the website through automated means</li>
                <li>Reproduce, duplicate, or copy any content without written permission</li>
                <li>Impersonate any person or entity</li>
              </ul>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Disclaimers</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our services do not constitute legal advice. For legal matters, consult with qualified legal counsel. While we strive to provide accurate and current information, trade regulations are complex and subject to change. We recommend clients:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Verify regulatory requirements with appropriate government agencies</li>
                <li>Maintain ongoing compliance monitoring</li>
                <li>Seek legal counsel for interpretation of laws and regulations</li>
              </ul>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Modifications to Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after changes are posted constitutes acceptance of the modified Terms.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Severability</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force and effect.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Entire Agreement</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms, together with any engagement letter and related agreements, constitute the entire agreement between you and Blackbird Trade Group regarding the use of our website and services.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <p className="text-gray-900 font-semibold mb-2">Blackbird Trade Group</p>
                <p className="text-gray-700">Email: nwood@blackbirdtradegroup.com</p>
                <p className="text-gray-700">Phone: +1 (904) 512-1329</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfService;
